import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ReplaySubject } from 'rxjs';

import { DocumentInfoTypeEnum, PositionsColumnModel, PositionTypeEnum } from '../../document-viewer.model';
import { PositionsModel } from 'projects/workspace/src/app/outgoing-invoice/models';
import { ProductTypes } from '../../../products/product-types';
import {
  PositionsContextMenuComponent,
  PositionsGroupContextMenuComponent
} from 'projects/workspace/src/app/shared/components';
import { PdfSettingsDesignModel, PdfSettingsModel, PdfSettingsPositionsModel } from 'projects/workspace/src/app/pdf-settings/models';
import {
  PdfSettingsAlignEnum,
  PdfSettingsDiscountUnitEnum,
  PdfSettingsProductIdTypeEnum,
  PdfSettingsTableRowStyleEnum
} from 'projects/workspace/src/app/pdf-settings/enums';
import { CustomerTypeEnum } from '../../../../models';
import { PartnersTypeEnum } from 'projects/workspace/src/app/partners/corporate/enums';
import { PositionMediaTypeEnum, PriceOptionEnum } from 'projects/workspace/src/app/shared/enums';
import { PositionsActionsType } from '../../../ui-components/table/custom-table.enums';
import { PositionGroupModel } from 'projects/workspace/src/app/shared/models';
import { logger } from 'codelyzer/util/logger';

@Component({
  selector: 'rnpl-positions-table',
  templateUrl: './positions-table.component.html',
  styleUrls: ['./positions-table.component.scss']
})
export class PositionsTableComponent implements OnChanges, OnDestroy {

  public infoTypeEnum = DocumentInfoTypeEnum;
  public mediaTypeEnum = PositionMediaTypeEnum;
  public rowStyleEnum = PdfSettingsTableRowStyleEnum;
  public productIdTypeEnum = PdfSettingsProductIdTypeEnum;
  public priceOptionEnum = PriceOptionEnum;
  public alignEnum = PdfSettingsAlignEnum;
  public discountUnitEnum = PdfSettingsDiscountUnitEnum;

  public selectedPositionsList: any = {
    [ProductTypes.GOODS]: [],
    [ProductTypes.SERVICES]: [],
    [ProductTypes.DIGITAL]: [],
  };
  public columns: PositionsColumnModel[] = [];
  // public _totalDiscountNet: number|string;
  // public _totalDiscountGross: number|string;
  public deliveryDateColumnName: string;
  public deliveryDatePropKey: string;
  public blockDnDWhileReordering: boolean = false;

  @Input() public positionsColumn: PositionsColumnModel[] = [];
  @Input() public pdfSettings: PdfSettingsModel;
  @Input() public positions: any;
  @Input() public startCount: number = 0;
  @Input() public isReadOnly: boolean = true;
  @Input() public deletable: boolean = true;
  @Input() public selectable: boolean = true;
  @Input() public tableTitle: string;
  @Input() public skipInfoBtn: boolean;
  @Input() public showShipInfo: boolean = false;
  @Input() public showBillInfo: boolean = false;
  @Input() public showMoveToGroup: boolean = true;
  @Input() public isSingleSelected: boolean;
  @Input() public isSmallBusiness: boolean = false;
  @Input() public showDiscount: boolean = false;
  @Input() public vatDisabled: boolean = false;
  // TODO: remove this input after show a dash for disabled VAT in all documents configured
  @Input() public showDashForDisabledVat: boolean = false;
  @Input() public showSubtotalForProductTypes: boolean = true;
  @Input() public negativeTotalAmount: boolean = false;
  @Input() public canChangeOrder: boolean = true;
  @Input() public totalAmountNet: number;
  @Input() public totalAmountGross: number;
  @Input() public partnerType: CustomerTypeEnum|PartnersTypeEnum;
  @Input() public lang: string;
  @Input() public priceOption: PriceOptionEnum;
  @Input() public group: PositionGroupModel;
  @Input() public allGroups: PositionGroupModel[];
  // @Input() set totalDiscountNet(value: number) {
  //   if (typeof value === 'string' && +value) {
  //     // for format '0,00', '10,56'
  //     this._totalDiscountNet = `-${value}`;
  //     return;
  //   }
  //   // for format 0, 540
  //   this._totalDiscountNet = +value ? (+value * -1) : value;
  // };
  // @Input() set totalDiscountGross(value: number) {
  //   if (typeof value === 'string' && +value) {
  //     this._totalDiscountGross = `-${value}`;
  //     return;
  //   }
  //   this._totalDiscountGross = +value ? (+value * -1) : value;
  // };
  @Input('selected') set selectedRowsSetter(rows: any) {
    this.selectedPositionsList = rows;
  }

  @Output() tableRowActionEmit: EventEmitter<any> = new EventEmitter();
  @Output() selectedPosition: EventEmitter<any> = new EventEmitter();
  @Output() actionListEmit: EventEmitter<any> = new EventEmitter();
  @Output() actionListGroupEmit: EventEmitter<any> = new EventEmitter();
  @Output() blockDnDWhileReorderingEmit: EventEmitter<boolean> = new EventEmitter();

  @ViewChild('contextMenuComponent', {static: false}) public contextMenuComponent: PositionsContextMenuComponent;
  @ViewChild('contextGroupMenuComponent', {static: false}) public contextGroupMenuComponent: PositionsGroupContextMenuComponent;

  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor() {
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (
        changes.hasOwnProperty('positionsColumn')
        || changes.hasOwnProperty('pdfSettings')
        || changes.hasOwnProperty('showDiscount')
        || changes.hasOwnProperty('partnerType')
        || changes.hasOwnProperty('vatDisabled')
        || changes.hasOwnProperty('isSmallBusiness')
        || changes.hasOwnProperty('priceOption')
      ) {
        this.filterColumns();
      }

      if (changes.hasOwnProperty('positions') && this.blockDnDWhileReordering) {
        this.blockDnDWhileReordering = false;
        this.blockDnDWhileReorderingEmit.emit(false);
      }

      if (changes.hasOwnProperty('positions') && this.positions[0] && this.positions[0].positionColumns) {
        const maxColumnsLength = this.positions.reduce((max, current) =>
          current.positionColumns.length > max.positionColumns.length ? current : max
        );
        // set columns for group
        this.positionsColumn = maxColumnsLength.positionColumns;
        this.filterColumns();
      }
    }
  }

  private filterColumns(): void {
    if (!this.pdfSettings || !this.positionsColumn.length) { return; }

    const filteredColumns = this.positionsColumn
      .filter((col: PositionsColumnModel) => !col.hasOwnProperty('showCondition') || col.showCondition)
      .filter((col: PositionsColumnModel) => col.type !== DocumentInfoTypeEnum.DISCOUNT || (this.positionsSettings.showDiscountOnPosition && this.showDiscount))
      .filter((col: PositionsColumnModel) => col.type !== DocumentInfoTypeEnum.VAT || (this.positionsSettings.showColumnVat && !this.isSmallBusiness))
      .filter((col: PositionsColumnModel) => col.type !== DocumentInfoTypeEnum.UNIT_PRICE || this.positionsSettings.showColumnUnitPrice)
      .filter((col: PositionsColumnModel) => col.type !== DocumentInfoTypeEnum.QUANTITY || this.positionsSettings.showColumnQty)
      .filter((col: PositionsColumnModel) => col.type !== DocumentInfoTypeEnum.DELIVERY_DATE || this.positionsSettings.showColumnDeliveryDate);

    this.columns = this.preparePricesColumns(filteredColumns);
  }

  public preparePricesColumns(columns: PositionsColumnModel[]): PositionsColumnModel[] {
    const unitPriceTitle: {[key in PriceOptionEnum]: string} = {
      [PriceOptionEnum.NET]: 'COLUMN.UNIT_PRICE_NET',
      [PriceOptionEnum.GROSS]: 'COLUMN.UNIT_PRICE_GROSS',
    };
    const amountPriceTitle: {[key in PriceOptionEnum]: string} = {
      [PriceOptionEnum.NET]: 'COLUMN.AMOUNT_NET',
      [PriceOptionEnum.GROSS]: 'COLUMN.AMOUNT_GROSS',
    };
    const discountPriceTitle: {[key in PriceOptionEnum]: string} = {
      [PriceOptionEnum.NET]: 'COMMON.DISCOUNT_NET',
      [PriceOptionEnum.GROSS]: 'COMMON.DISCOUNT_GROSS',
    };
    const priceProp: {[key in PriceOptionEnum]: string} = {
      [PriceOptionEnum.NET]: 'propNet',
      [PriceOptionEnum.GROSS]: 'propGross',
    };

    return columns
      .filter((col: PositionsColumnModel) => {
        if (col.type !== DocumentInfoTypeEnum.DELIVERY_DATE) {
          return true;
        }
        this.deliveryDateColumnName = col.columnName;
        this.deliveryDatePropKey = col.prop;
        return false;
      })
      .map((col: PositionsColumnModel) => {
      if (col.type === DocumentInfoTypeEnum.DISCOUNT) {
        col = {
          ...col,
          width: col.width || 124,
          // replace prop with right option (for net or gross) based on conditions
          prop: this.positionsSettings.discountUnit === PdfSettingsDiscountUnitEnum.AMOUNT
            ? this.isSmallBusiness
              ? col.propNet
              : col[priceProp[this.priceOption]]
            : col.propDiscountPercent,
          columnName: this.positionsSettings.discountUnit === PdfSettingsDiscountUnitEnum.AMOUNT
            ? this.isSmallBusiness
              ? discountPriceTitle[PriceOptionEnum.NET]
              : discountPriceTitle[this.priceOption]
            : 'COMMON.DISCOUNT',
          additionalText: this.positionsSettings.discountUnit === PdfSettingsDiscountUnitEnum.AMOUNT
            ? ' €'
            : '%',
          currencySourceFormat: this.positionsSettings.discountUnit === PdfSettingsDiscountUnitEnum.AMOUNT
            ? col.currencySourceFormat || false
            : true
        };
      }

      if (col.type === DocumentInfoTypeEnum.UNIT_PRICE || col.type === DocumentInfoTypeEnum.AMOUNT_PRICE) {
        col = {
          ...col,
          additionalText: ' €', // todo: add from workspace currency
          // replace prop with right option (for net or gross) based on conditions
          prop: this.isSmallBusiness
            ? col.propNet
            : col[priceProp[this.priceOption]],
        };
      }

      if (col.type === DocumentInfoTypeEnum.UNIT_PRICE) {
        return {
          ...col,
          width: col.width || 144,
          type: DocumentInfoTypeEnum.CURRENCY, // after filtering and preparing change type
          columnName: this.isSmallBusiness
            ? unitPriceTitle[PriceOptionEnum.NET]
            : unitPriceTitle[this.priceOption],
        };
      }

      if (col.type === DocumentInfoTypeEnum.AMOUNT_PRICE) {
        return {
          ...col,
          width: col.width || 128,
          type: DocumentInfoTypeEnum.CURRENCY, // after filtering and preparing change type
          columnName: this.isSmallBusiness
            ? amountPriceTitle[PriceOptionEnum.NET]
            : amountPriceTitle[this.priceOption],
        };
      }

      // if (col.type === DocumentInfoTypeEnum.DELIVERY_DATE) {
      //   col = {
      //     ...col,
      //     width: col.width || 160,
      //   }
      // }

      if (col.type === DocumentInfoTypeEnum.QUANTITY) {
        col = {
          ...col,
          width: col.width || 128,
        };
      }

      if (col.type === DocumentInfoTypeEnum.VAT) {
        col = {
          ...col,
          width: col.width || 72,
          prop: this.vatDisabled
            ? ''
            : col.prop,
          additionalText: this.vatDisabled
            ? ''
            : '%',
        };
      }

      return col;
    });
  }


  public onContextMenu(event: MouseEvent, item: any): void {
    event.preventDefault();
    event.stopPropagation();

    this.contextMenuComponent.onContextMenu(event, item);
  }

  public onGroupContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.contextGroupMenuComponent.onContextMenu(event);
  }

  public tableRowAction(position: PositionsModel, index: number, positions): void {
    // if (this.isReadOnly) { return; }
    this.tableRowActionEmit.emit({position: position, rowIndex: index, positions: positions});
  }

  public onSelect(selected, productType): void {
    if (this.isSingleSelected) {

      if (!this.selectedPositionsList.includes(selected)) {
        this.selectedPositionsList.push(selected);
      } else {
        this.selectedPositionsList = this.selectedPositionsList.filter(item => item.id !== selected.id);
      }
    } else {
      if (!this.selectedPositionsList[productType] || !this.selectedPositionsList[productType].includes(selected)) {
        if (!this.selectedPositionsList[productType]) {
          this.selectedPositionsList[productType] = [];
        }
        this.selectedPositionsList[productType].push(selected);
      } else {
        this.selectedPositionsList[productType] = this.selectedPositionsList[productType]
          .filter(item => (item.id || item.positionId) !== (selected.id || selected.positionId));
      }
    }

    this.selectedPosition.emit(this.selectedPositionsList);
  }

  public isSelectedPosition(position): boolean {
    if (!this.selectedPositionsList) { return false; }

    if (!this.isSingleSelected) {
      if (!this.selectedPositionsList[position.productTypeForGrouping || position.productType]) { return false; }
      return this.selectedPositionsList[position.productTypeForGrouping || position.productType].includes(position);
    }

    return this.selectedPositionsList.includes(position);
  }

  public actionListHandler(e) {
    this.actionListEmit.next(e);
  }

  public actionListGroupHandler(e): void {
    this.actionListGroupEmit.emit(e);
  }

  // public hideProductLinkCondition(positionType: PositionTypeEnum): boolean {
  //   return positionType === PositionTypeEnum.DELIVERY_STANDARD ||
  //          positionType === PositionTypeEnum.DELIVERY_EXPRESS ||
  //          positionType === PositionTypeEnum.PREPAYMENT ||
  //          positionType === PositionTypeEnum.INVOICE ||
  //          positionType === PositionTypeEnum.INSURANCE;
  // }

  public getNegativeTotalAmountNet(): number|string {
    if (typeof this.totalAmountNet === 'string' && +this.totalAmountNet) {
      // for format '0,00', '10,56'
      return `-${this.totalAmountNet}`;
    }
    // for format 0, 540
    return this.totalAmountNet ? (+this.totalAmountNet * -1) : this.totalAmountNet;
  }

  public getNegativeTotalAmountGross(): number|string {
    if (typeof this.totalAmountGross === 'string' && +this.totalAmountGross) {
      // for format '0,00', '10,56'
      return `-${this.totalAmountGross}`;
    }
    // for format 0, 540
    return this.totalAmountGross ? (+this.totalAmountGross * -1) : this.totalAmountGross;
  }

  public get designSettings(): PdfSettingsDesignModel {
    if (!this.pdfSettings) { return null; }

    return this.pdfSettings.settings.design;
  }

  public get positionsSettings(): PdfSettingsPositionsModel {
    if (!this.pdfSettings) { return null; }

    return this.pdfSettings.settings.positions;
  }

  get isPartnerPrivate(): boolean {
    return this.partnerType && this.partnerType === CustomerTypeEnum.PRIVATE_PARTNER;
  }

  public drop(event: CdkDragDrop<any[]>) {
    if (event.currentIndex === event.previousIndex) { return; }

    const position = { ...this.positions[event.previousIndex] };
    const nextPositionOrder = this.positions[event.currentIndex].order;

    moveItemInArray(this.positions, event.previousIndex, event.currentIndex);
    this.blockDnDWhileReordering = true;
    this.blockDnDWhileReorderingEmit.emit(true);

    this.actionListEmit.next({
      actionType: PositionsActionsType.TO,
      row: position,
      moveTo: nextPositionOrder,
    });
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
